// One audit's accordion group inside the select-work-steps table. Groups are
// collapsed and UNFETCHED by default: this placeholder renders just the
// header row (name + the work-step count already carried from step 1's
// listing) and mounts WorkStepAuditGroupLoaded — which owns the audit's
// paginated fetch — on first expand, so cost scales with what the user
// actually opens, not with how many audits they selected. Checking the
// placeholder's select-all box both activates the group and selects it once
// the first page lands. (Search doesn't route through these groups at all —
// a term switches the table to WorkStepSearchGroup buckets fed by one
// batched request.)
import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { Checkbox } from "../../../../../components/ui/checkbox";
import { TableCell, TableRow } from "../../../../../components/ui/table";
import {
  OptroOpsAuditSummary,
  OptroWorkStepSummary,
} from "../../../../../models/audit/workStep";
import { WorkStepAuditGroupLoaded } from "./WorkStepAuditGroupLoaded";

const COLUMN_COUNT = 5;

interface WorkStepAuditGroupProps {
  audit: OptroOpsAuditSummary;
  search: string;
  isSelected: (externalRef: string) => boolean;
  onToggle: (workStep: OptroWorkStepSummary) => void;
  selectMany: (workSteps: OptroWorkStepSummary[]) => void;
  deselectMany: (externalRefs: string[]) => void;
}

export const WorkStepAuditGroup = (props: WorkStepAuditGroupProps) => {
  const { audit } = props;
  const [activated, setActivated] = useState(false);
  const [autoSelectAll, setAutoSelectAll] = useState(false);

  if (activated) {
    return (
      <WorkStepAuditGroupLoaded {...props} autoSelectAll={autoSelectAll} />
    );
  }

  return (
    <TableRow
      className="bg-stone-50 hover:bg-stone-100 cursor-pointer"
      onClick={() => setActivated(true)}
    >
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={false}
          onCheckedChange={() => {
            setAutoSelectAll(true);
            setActivated(true);
          }}
          aria-label={`Select all work steps in ${audit.name}`}
        />
      </TableCell>
      <TableCell colSpan={COLUMN_COUNT - 1} className="font-semibold">
        <div className="flex items-center gap-2">
          <ChevronRight className="h-4 w-4 text-stone-500" />
          <span>{audit.name}</span>
          {audit.workStepCount !== null && (
            <span className="text-sm font-normal text-stone-500">
              {audit.workStepCount} work step
              {audit.workStepCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};
